import { useEffect, useState } from 'react'
import { Alert, CircularProgress, Sheet, Stack, Typography } from '@mui/joy'
import { CrmContact, getCompanyContacts } from 'api/oracleApi'

interface Props {
    companyId: string
}

export function CompanyContactsList({ companyId }: Props) {
    const [contacts, setContacts] = useState<CrmContact[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setLoading(true)
        getCompanyContacts(companyId)
            .then(setContacts)
            .catch((err) => setError(err?.message || 'Failed to load contacts'))
            .finally(() => setLoading(false))
    }, [companyId])

    if (loading)
        return (
            <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size="sm" />
            </Stack>
        )
    if (error) return <Alert color="danger">{error}</Alert>
    if (contacts.length === 0)
        return (
            <Typography level="body-sm" color="neutral">
                No contacts captured yet
            </Typography>
        )

    return (
        <Stack gap={1}>
            <Typography level="title-sm">Contacts ({contacts.length})</Typography>
            {contacts.map((c) => (
                <Sheet key={c.contact_id} variant="outlined" sx={{ borderRadius: 'sm', p: 1.25 }}>
                    <Stack gap={0.25}>
                        <Typography level="body-sm" fontWeight="lg">
                            {c.name || '(no name)'}
                            {c.title ? ` — ${c.title}` : ''}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                            {[c.email, c.phone].filter(Boolean).join(' | ') || '(no contact details)'} | source: {c.source}
                        </Typography>
                    </Stack>
                </Sheet>
            ))}
        </Stack>
    )
}
